import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { LANG } from './shared/common/const/common.const';
import { ConfigService } from './shared/services/config.service';
import { LocalStorageService } from './shared/services/storage/local-storage.service';
import { KEYS } from './shared/utils/constants/storage.constants';
import { StorefrontService } from './storefront/storefront.service';

// * This service is used to change the language of the app, that is way needs to be in the root. Otherwise, it will create diferrents variables for each module.
@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  private language = {
    en: 'English',
    te: 'Telugu',
    ta: 'Tamil',
    // ml: 'Malayalam', // not supported yet
  };
  currLang: string = 'en';
  private languageChangeObservable: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  constructor(
    private translate: TranslateService,
    private storefrontService: StorefrontService,
    private readonly localStorageService: LocalStorageService,
    private http: HttpClient,
    private configService: ConfigService
  ) {
    // translate.addLangs(['en', 'ta', 'te']); // 'ml' is not supported yet
    translate.setDefaultLang('en');
  }

  getAndSetMergedTranslations(lang: string): void {
    this.translate.getTranslation(lang).subscribe((localTranslations) => {
      const url =
        this.configService.applicationConfig.smarttvV2LocalizationURL.replace(
          '%',
          lang
        );
      if (url.length > 0) {
        this.http.get(url).subscribe(
          (remoteTranslations: any) => {
            const merged = this.mergeMissingKeys(
              localTranslations,
              remoteTranslations
            );
            this.translate.setTranslation(lang, merged, false);
            this.translate.use(lang);
            this.currLang = lang;
          },
          (error: any) => {
            console.error('Error fetching remote translations:', error);
            this.translate.use(lang);
            this.currLang = lang;
          }
        );
      } else {
        this.translate.use(lang);
      }
    });
  }

  private mergeMissingKeys(local: any, remote: any): any {
    if (!local) return remote;
    if (!remote) return local;

    const result = { ...remote };

    for (const key in local) {
      if (local.hasOwnProperty(key)) {
        if (!result.hasOwnProperty(key)) {
          result[key] = local[key];
        } else if (
          typeof local[key] === 'object' &&
          local[key] !== null &&
          typeof result[key] === 'object' &&
          result[key] !== null &&
          !Array.isArray(local[key]) &&
          !Array.isArray(result[key])
        ) {
          result[key] = this.mergeMissingKeys(local[key], result[key]);
        }
      }
    }
    return result;
  }

  getObservable(): Observable<boolean> {
    return this.languageChangeObservable.asObservable();
  }

  propagateChange(loadTabs: boolean): void {
    this.languageChangeObservable.next(loadTabs);
  }

  tempChangeLanguage(language: string): void {
    // this.translate.use( || '');
    this.getAndSetMergedTranslations(language);
  }

  setDefault() {
    const lang = this.localStorageService.getItem('lang');
    this.getAndSetFontFamily(lang);
    // this.translate.use(lang);
    this.getAndSetMergedTranslations(lang);
  }

  changeDisplayLanguage(language: string): void {
    const langKey = this.getKeyByValue(language) || 'en';
    this.currLang = langKey;
    this.updateDisplayLanguageWithLangKey(langKey);
  }

  updateDisplayLanguageWithLangKey(langKey: string): void {
    this.getAndSetFontFamily(langKey);
    // this.translate.use(langKey || '');
    this.getAndSetMergedTranslations(langKey);
    this.localStorageService.setItem(KEYS.DISPLAY_LANGUAGE, langKey);
  }

  private getAndSetFontFamily(langKey: string): void {
    const rootElement = document.documentElement;
    const fontFamily = this.getFontFamily(langKey);
    rootElement.style.setProperty('--app-font-family', fontFamily);
  }

  private getFontFamily(langKey: string): string {
    const fontFamilyMap: Record<string, string> = {
      [LANG.TG.k]: "'NotoSans-Telugu','Inter'",
      [LANG.TM.k]: "'NotoSans-Tamil','Inter'",
    };
    return fontFamilyMap[langKey] || 'Inter';
  }

  changeWatchLanguage(lang: string): void {
    const watchLang = this.getKeyByValue(lang) || '';
    this.localStorageService.setItem(KEYS.WATCH_LANGUAGE, watchLang);
    const currentDisplayLang = this.localStorageService.getItem(
      KEYS.DISPLAY_LANGUAGE
    );
    const displayLang =
      currentDisplayLang === watchLang ? currentDisplayLang : 'en';
    this.localStorageService.setItem(KEYS.DISPLAY_LANGUAGE, displayLang);
    // this.translate.use('en');
    this.getAndSetMergedTranslations(displayLang);
    this.getAndSetFontFamily(displayLang);
    this.storefrontService.initialiseServices();
    // this.getCalogueSubscription();
  }

  getCurrentWatchLanguage(): string {
    const lang = this.localStorageService.getItem(KEYS.WATCH_LANGUAGE);
    if (lang) {
      return (this.language as Record<string, string>)[lang] || '';
    } else {
      return '';
    }
  }

  getCurrentDisplayLanguage(): string {
    const lang = this.localStorageService.getItem(KEYS.DISPLAY_LANGUAGE);
    if (lang) {
      return (this.language as Record<string, string>)[lang] || '';
    } else {
      return '';
    }
  }

  // getCalogueSubscription(): void {
  //   this.service.getAccountProfileUserRequest().subscribe((account) => {
  //     getSubscriptionStatus(account);
  //   });
  // }

  getKeyByValue(value: string): string | undefined {
    return Object.keys(this.language).find(
      (key) => (this.language as Record<string, string>)[key] === value
    );
  }

  getLanguage(lg: string): string {
    return (this.language as Record<string, string>)[lg] || '';
  }

  getTranslatedTextWithParams(key: string, params: any[]) {
    let translatedText = this.translate.instant(key);
    for (let i = 0; i < params.length; i++) {
      translatedText = translatedText.replace(`{${i}}`, String(params[i]));
    }
    return translatedText;
  }

  getTranslatedText(key: string) {
    return this.translate.instant(key);
  }
}
